import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, orderBy, serverTimestamp, Timestamp, limit, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { subscribeShared } from '../lib/sharedQuery';
import { useAuth } from '../contexts/AuthContext';
import type { Notification as NotificationType, CreateNotificationData, NotificationStats, NotificationSettings } from '../types/notification';
import { getEmailTemplateType } from '../config/notificationTemplateMapping';
import { emailService } from '../services/emailService';

// Modul szintű állapot az asztali értesítéshez: a megosztott figyelő egyszer fut, bárhány komponens használja a hookot
let latestSettings: NotificationSettings | null = null;
let firstSnapshotDone = false;
const shownIds = new Set<string>();
let settingsCache: { key: string; value: NotificationSettings | null; promise: Promise<void> | null } | null = null;

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  useEffect(() => { latestSettings = settings; }, [settings]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Értesítések betöltése
  useEffect(() => {
    if (!user?.id || !user?.organizationId) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const uid = user.id, orgId = user.organizationId;
    const unsubscribe = subscribeShared<NotificationType>(
      `notifications:${uid}:${orgId}`,
      () => query(collection(db, 'notifications'), where('userId', '==', uid), where('organizationId', '==', orgId), orderBy('createdAt', 'desc'), limit(100)),
      (snapshot) => {
        const list = snapshot.docs.map((doc) => {
          const data = doc.data();
          return { id: doc.id, ...data, createdAt: data.createdAt?.toDate() || new Date(), readAt: data.readAt?.toDate() || undefined, expiresAt: data.expiresAt?.toDate() || undefined } as NotificationType;
        });
        // Böngésző (asztali) értesítés az ÚJ, olvasatlan bejegyzésekről – a címzett gépén.
        // Első betöltésnél nem; 2 percnél régebbieket (pl. újratöltés) kihagyjuk.
        if (!firstSnapshotDone) { firstSnapshotDone = true; return list; }
        const st = latestSettings;
        if (st?.desktopNotifications && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
          const cutoff = Date.now() - 2 * 60000;
          snapshot.docChanges().forEach(ch => {
            if (ch.type !== 'added') return;
            const d = ch.doc.data();
            if (d.isRead || shownIds.has(ch.doc.id)) return;
            const created = d.createdAt?.toDate?.()?.getTime?.() || Date.now();
            if (created < cutoff) return;
            shownIds.add(ch.doc.id);
            try {
              const n = new Notification(d.title || 'YouCRM', { body: d.message || '', icon: '/icons/icon-192.png', tag: ch.doc.id, silent: !st.soundEnabled } as NotificationOptions);
              n.onclick = () => { try { window.focus(); if (d.actionUrl) window.location.href = d.actionUrl; n.close(); } catch { /* ignore */ } };
            } catch { /* pl. mobil böngésző */ }
          });
        }
        return list;
      },
      (data, err) => {
        if (err) { console.error('Error fetching notifications:', err); setError('Failed to load notifications'); setLoading(false); return; }
        setNotifications(data || []);
        setLoading(false);
        setError(null);
      }
    );

    return unsubscribe;
  }, [user?.id, user?.organizationId]);

  // Beállítások betöltése
  useEffect(() => {
    if (!user?.id || !user?.organizationId) {
      setSettings(null);
      return;
    }

    const loadSettings = async () => {
      const key = `${user.id}:${user.organizationId}`;
      // Megosztott cache: a hook 40+ helyen fut, de a beállítást csak egyszer olvassuk
      if (settingsCache && settingsCache.key === key) {
        if (settingsCache.promise) await settingsCache.promise;
        if (settingsCache.value) { setSettings(settingsCache.value); return; }
      }
      try {
        const q = query(
          collection(db, 'notificationSettings'),
          where('userId', '==', user.id),
          where('organizationId', '==', user.organizationId)
        );

        const p = getDocs(q);
        settingsCache = { key, value: null, promise: p.then(() => undefined).catch(() => undefined) };
        const querySnapshot = await p;
        
        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const data = doc.data();
          const loaded = { id: doc.id, ...data, updatedAt: data.updatedAt?.toDate() || new Date() } as NotificationSettings;
          settingsCache = { key, value: loaded, promise: null };
          setSettings(loaded);
        } else {
          // Alapértelmezett beállítások létrehozása
          await createDefaultSettings();
        }
      } catch (error) {
        console.error('Error loading notification settings:', error);
      }
    };

    loadSettings();
  }, [user?.id, user?.organizationId]);

  // Alapértelmezett beállítások létrehozása
  const createDefaultSettings = async (): Promise<void> => {
    if (!user?.id || !user?.organizationId) return;

    const defaultSettings: Omit<NotificationSettings, 'id'> = {
      userId: user.id,
      organizationId: user.organizationId,
      emailNotifications: {
        taskAssigned: true,
        taskCompleted: false,
        taskOverdue: true,
        projectUpdates: true,
        deadlineReminders: true,
        systemNotifications: true,
        customerUpdates: true,
        quotationUpdates: true,
        dealUpdates: false,
        contractUpdates: true,
        organizationUpdates: true,
        userUpdates: true,
        formSubmissions: true,
        appointmentUpdates: true,
      },
      inAppNotifications: {
        taskAssigned: true,
        taskCompleted: true,
        taskOverdue: true,
        projectUpdates: true,
        deadlineReminders: true,
        systemNotifications: true,
        mentions: true,
        customerUpdates: true,
        quotationUpdates: true,
        dealUpdates: true,
        contractUpdates: true,
        organizationUpdates: true,
        userUpdates: true,
        formSubmissions: true,
        appointmentUpdates: true,
      },
      soundEnabled: true,
      desktopNotifications: false,
      debugMode: false, // Alapértelmezetten kikapcsolva
      quietHours: {
        enabled: false,
        startTime: '22:00',
        endTime: '08:00',
      },
      updatedAt: new Date(),
    };

    try {
      const docRef = await addDoc(collection(db, 'notificationSettings'), {
        ...defaultSettings,
        updatedAt: serverTimestamp(),
      });

      setSettings({
        id: docRef.id,
        ...defaultSettings,
      });
    } catch (error) {
      console.error('Error creating default settings:', error);
    }
  };

  // Helper: Check if current time is in quiet hours
  const isInQuietHours = (quietHours: NotificationSettings['quietHours']): boolean => {
    if (!quietHours.enabled) return false;
    
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    
    const [startHour, startMin] = quietHours.startTime.split(':').map(Number);
    const [endHour, endMin] = quietHours.endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    // Handle overnight quiet hours (e.g., 22:00 to 08:00)
    if (startMinutes > endMinutes) {
      return currentMinutes >= startMinutes || currentMinutes < endMinutes;
    }
    
    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  };

  // Helper: Map notification type to settings key
  const getSettingsKey = (type: string): string | null => {
    const typeMap: Record<string, string> = {
      'task_assigned': 'taskAssigned',
      'task_completed': 'taskCompleted',
      'task_overdue': 'taskOverdue',
      'task_status_changed': 'taskAssigned', // Use taskAssigned setting
      'project_created': 'projectUpdates',
      'project_updated': 'projectUpdates',
      'project_status_changed': 'projectUpdates',
      'project_update': 'projectUpdates', // Legacy type
      'deadline_reminder': 'deadlineReminders',
      'system': 'systemNotifications',
      'mention': 'mentions',
      'customer': 'customerUpdates', // Legacy type
      'customer_created': 'customerUpdates',
      'customer_updated': 'customerUpdates',
      'quotation': 'quotationUpdates', // Legacy type
      'quotation_created': 'quotationUpdates',
      'quotation_updated': 'quotationUpdates',
      'deal': 'dealUpdates', // Legacy type
      'deal_created': 'dealUpdates',
      'deal_updated': 'dealUpdates',
      'contract': 'contractUpdates', // Legacy type
      'contract_created': 'contractUpdates',
      'contract_updated': 'contractUpdates',
      'organization_updated': 'organizationUpdates',
      'user_invited': 'userUpdates',
      'user_updated': 'userUpdates',
      'form_submitted': 'formSubmissions',
      'appointment_created': 'appointmentUpdates',
      'appointment_updated': 'appointmentUpdates',
    };
    
    return typeMap[type] || null;
  };

  // Új értesítés létrehozása
  const createNotification = async (data: CreateNotificationData): Promise<string> => {
    try {
      // ✅ 1. Ellenőrizzük, hogy vannak-e beállítások
      if (!settings) {
        console.warn('No notification settings found, using defaults');
        // Ha nincs settings, alapértelmezetten engedélyezzük
      }

      // ✅ 2. Csendes órák ellenőrzése
      if (settings?.quietHours && isInQuietHours(settings.quietHours)) {
        console.log(`🔕 Quiet hours active (${settings.quietHours.startTime} - ${settings.quietHours.endTime}), notification suppressed:`, data.type);
        return ''; // Ne hozzunk létre értesítést csendes órákban
      }

      // ✅ 3. In-App notification beállítás ellenőrzése
      const settingsKey = getSettingsKey(data.type);
      if (settingsKey && settings?.inAppNotifications) {
        const inAppSettings = settings.inAppNotifications as Record<string, boolean>;
        const isEnabled = inAppSettings[settingsKey];
        if (!isEnabled) {
          console.log(`🔕 In-app notification disabled for type: ${data.type} (${settingsKey})`);
          return ''; // Ne hozzunk létre értesítést, ha ki van kapcsolva
        }
      }

      // ✅ 4. Értesítés létrehozása Firestore-ban
      const notificationData = {
        ...data,
        isRead: false,
        createdAt: serverTimestamp(),
        readAt: null,
      };

      const docRef = await addDoc(collection(db, 'notifications'), notificationData);
      console.log('✅ Notification created successfully:', docRef.id, data.type);
      
      // ✅ 5. Email notification ellenőrzése (ha be van kapcsolva)
      if (settingsKey && settings?.emailNotifications) {
        // Check if the key exists in emailNotifications (mentions is only in inAppNotifications)
        const emailSettings = settings.emailNotifications as Record<string, boolean>;
        if (settingsKey in emailSettings && emailSettings[settingsKey]) {
          console.log('📧 Email notification enabled for:', data.type);
          
          try {
            // Get the appropriate email template type
            const templateType = getEmailTemplateType(data.type);
            console.log('📧 Using email template type:', templateType);
            
            // Get user email from userId
            const userDoc = await getDocs(
              query(collection(db, 'users'), where('__name__', '==', data.userId), limit(1))
            );
            
            if (!userDoc.empty) {
              const userData = userDoc.docs[0].data();
              const userEmail = userData.email;
              
              if (userEmail) {
                await emailService.sendEmail({
                  to: userEmail,
                  templateType: templateType,
                  templateData: {
                    userName: userData.displayName || userData.email,
                    subject: data.title,
                    message: data.message,
                    actionUrl: data.actionUrl,
                    actionText: 'Megtekintés',
                    organizationName: userData.organizationName || 'YouCRM',
                    date: new Date().toLocaleDateString('hu-HU'),
                    ...data.data
                  },
                  organizationId: data.organizationId
                });
                
                console.log('✅ Email sent successfully to:', userEmail);
              }
            }
          } catch (emailError) {
            console.error('❌ Error sending email notification:', emailError);
            // Ne akadályozzuk meg az értesítés létrehozását, ha az email küldés sikertelen
          }
        }
      }

      // 6. Asztali értesítés: a címzett böngészőjében jelenik meg (onSnapshot), nem a küldőében

      // ✅ 7. Hang lejátszása (ha engedélyezve van)
      if (settings?.soundEnabled) {
        // TODO: Notification sound lejátszása
        // playNotificationSound();
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating notification:', error);
      console.warn('Notification creation failed, but continuing operation...');
      return ''; // Return empty string instead of throwing
    }
  };

  // Értesítés megjelölése olvasottként
  const markAsRead = async (notificationId: string): Promise<void> => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        isRead: true,
        readAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  };

  // Összes értesítés megjelölése olvasottként
  const markAllAsRead = async (): Promise<void> => {
    try {
      const unreadNotifications = notifications.filter(n => !n.isRead);
      const promises = unreadNotifications.map(notification =>
        updateDoc(doc(db, 'notifications', notification.id), {
          isRead: true,
          readAt: serverTimestamp(),
        })
      );

      await Promise.all(promises);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  };

  // Értesítés törlése
  const deleteNotification = async (notificationId: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  };

  // Összes értesítés törlése
  const clearAllNotifications = async (): Promise<void> => {
    try {
      const promises = notifications.map(notification =>
        deleteDoc(doc(db, 'notifications', notification.id))
      );

      await Promise.all(promises);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      throw error;
    }
  };

  // Beállítások frissítése
  const updateSettings = async (newSettings: Partial<NotificationSettings>): Promise<void> => {
    settingsCache = null; // következő betöltés frissen olvas
    if (!settings?.id) return;

    try {
      await updateDoc(doc(db, 'notificationSettings', settings.id), {
        ...newSettings,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }
  };

  // Értesítési statisztikák
  const getStats = (): NotificationStats => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeek = new Date(today.getTime() - (today.getDay() * 24 * 60 * 60 * 1000));

    return {
      total: notifications.length,
      unread: notifications.filter(n => !n.isRead).length,
      today: notifications.filter(n => n.createdAt >= today).length,
      thisWeek: notifications.filter(n => n.createdAt >= thisWeek).length,
      overdue: notifications.filter(n => 
        n.type === 'task_overdue' || 
        n.type === 'deadline_reminder'
      ).length,
    };
  };

  // Szűrt értesítések
  const getUnreadNotifications = () => notifications.filter(n => !n.isRead);
  const getRecentNotifications = (count = 5) => notifications.slice(0, count);
  const getNotificationsByType = (type: NotificationType['type']) => 
    notifications.filter(n => n.type === type);

  return {
    notifications,
    settings,
    loading,
    error,
    createNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    updateSettings,
    getStats,
    getUnreadNotifications,
    getRecentNotifications,
    getNotificationsByType,
  };
}; 